import { useCallback, useEffect, useRef } from "react";
import type DashboardStore from "./DashboardStore";

/**
 * Attaches an IntersectionObserver to dashboard component groups.
 * Returns `attachRef(componentIds)` — a ref-callback factory that wires each
 * rendered group element to the observer.  When an element enters the viewport
 * (or comes within 350 px of the fold) the store is notified so it can enqueue
 * those components for data fetching.
 */
export function useViewportLazyLoad(store: DashboardStore | null) {
    const observerRef = useRef<IntersectionObserver | null>(null);
    // Stable map from DOM element → the component IDs it represents
    const elementIdsRef = useRef<Map<Element, string[]>>(new Map());
    // Memoized ref-callbacks keyed by sorted component IDs to prevent
    // unobserve/re-observe on every render from inline attachRef(ids) calls
    const callbackCacheRef = useRef<
        Map<string, (el: HTMLElement | null) => void>
    >(new Map());

    useEffect(() => {
        if (!store) return;

        // Clear cached ref-callbacks from the previous visit; visibleComponentIds is
        // kept as a load heuristic — the observer corrects it shortly after elements mount.
        callbackCacheRef.current.clear();

        let fallbackTimer: number | undefined;
        const notifyFromViewportGeometry = () => {
            const visible: string[] = [];
            const hidden: string[] = [];
            const viewportBottom = window.innerHeight + 350;

            elementIdsRef.current.forEach((ids, element) => {
                const rect = element.getBoundingClientRect();
                if (rect.bottom >= 0 && rect.top <= viewportBottom) {
                    visible.push(...ids);
                } else {
                    hidden.push(...ids);
                }
            });

            if (visible.length > 0) store.notifyComponentsVisible(visible);
            // Notify even when no cards are visible so the initial refresh cannot
            // remain blocked if observer setup fails or produces no entries.
            store.notifyComponentsHidden(hidden);
        };

        const scheduleGeometryFallback = () => {
            fallbackTimer = window.setTimeout(notifyFromViewportGeometry, 500);
        };

        const createGeometryFallbackCleanup = () => {
            const onViewportChanged = () => notifyFromViewportGeometry();
            window.addEventListener("scroll", onViewportChanged, {
                passive: true,
            });
            window.addEventListener("resize", onViewportChanged);
            scheduleGeometryFallback();

            return () => {
                if (fallbackTimer !== undefined) {
                    window.clearTimeout(fallbackTimer);
                }
                window.removeEventListener("scroll", onViewportChanged);
                window.removeEventListener("resize", onViewportChanged);
            };
        };

        if (typeof IntersectionObserver === "undefined") {
            return createGeometryFallbackCleanup();
        }

        let observer: IntersectionObserver;
        try {
            observer = new IntersectionObserver(
                (entries) => {
                    if (fallbackTimer !== undefined) {
                        window.clearTimeout(fallbackTimer);
                        fallbackTimer = undefined;
                    }
                    const visible: string[] = [];
                    const hidden: string[] = [];
                    for (const entry of entries) {
                        const ids =
                            elementIdsRef.current.get(entry.target) ?? [];
                        if (entry.isIntersecting) {
                            visible.push(...ids);
                        } else {
                            hidden.push(...ids);
                        }
                    }
                    if (visible.length > 0) store.notifyComponentsVisible(visible);
                    if (hidden.length > 0) store.notifyComponentsHidden(hidden);
                },
                {
                    threshold: 0,
                    // Pre-load components 350 px below the visible fold
                    rootMargin: "0px 0px 350px 0px",
                },
            );
        } catch {
            return createGeometryFallbackCleanup();
        }

        observerRef.current = observer;
        // Re-observe any elements already registered (e.g. store identity changed)
        elementIdsRef.current.forEach((_, el) => observer.observe(el));
        scheduleGeometryFallback();

        return () => {
            if (fallbackTimer !== undefined) {
                window.clearTimeout(fallbackTimer);
            }
            observer.disconnect();
            observerRef.current = null;
        };
    }, [store]);

    /**
     * Returns a stable ref-callback for a given set of component IDs.
     * Callbacks are memoized by their sorted ID key so React receives the same
     * function reference across renders and does not unobserve/re-observe the
     * element on every re-render.
     */
    const attachRef = useCallback(
        (componentIds: string[]) => {
            const key = [...componentIds].sort().join(",");
            const cached = callbackCacheRef.current.get(key);
            if (cached) return cached;

            const cb = (el: HTMLElement | null) => {
                // Remove any prior registration that shares one of these IDs
                elementIdsRef.current.forEach((ids, existingEl) => {
                    if (ids.some((id) => componentIds.includes(id))) {
                        observerRef.current?.unobserve(existingEl);
                        elementIdsRef.current.delete(existingEl);
                    }
                });
                if (el) {
                    elementIdsRef.current.set(el, componentIds);
                    observerRef.current?.observe(el);
                }
            };

            callbackCacheRef.current.set(key, cb);
            return cb;
        },
        [],
    );

    return { attachRef };
}
